import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  useDocumentsServeis,
  type DocumentAdminDto,
  type EstatDocument,
} from '@/api/administracio';
import { Desplegable } from '@/components/Desplegable';
import { Icona, type NomIcona } from '@/components/Icona';
import { useFormat } from '@/i18n/useFormat';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { Buit } from '@/pages/operacio/comu';
import { useMe } from '@/permisos/MeContext';
import { CercaAmbit } from './marc/CercaAmbit';
import { useEstructura, type NodePla } from './marc/estructura';
import { Cerca } from './comu';
import { badgeEstatDocument, midaLlegible } from './utils';
import { W34PujaDocument } from './W34PujaDocument';
import './administracio.css';
import './sistema.css';

const ESTATS: EstatDocument[] = ['PUBLICAT', 'ESBORRANY', 'BLOQUEJAT', 'RETIRAT'];
const ICONA_ESTAT: Record<EstatDocument, NomIcona> = {
  PUBLICAT: 'checkmark-circle',
  ESBORRANY: 'clock',
  BLOQUEJAT: 'lock',
  RETIRAT: 'archive',
};
const ORDRE_ESTAT: Record<EstatDocument, number> = {
  PUBLICAT: 0,
  ESBORRANY: 1,
  BLOQUEJAT: 2,
  RETIRAT: 3,
};
const PER_PAGINA = 25;
type Columna = 'document' | 'per' | 'actualitzat' | 'estat';

const normalitza = (s: string) =>
  s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

const extensio = (d: DocumentAdminDto) =>
  (d.versioVigent ?? d.darreraVersio)?.nomFitxer.split('.').pop()?.toLowerCase() ?? '';

export function W32Documentacio() {
  const { t } = useTranslation('administracio');
  const f = useFormat();
  const navigate = useNavigate();
  const { pot } = useMe();
  const e = useEstructura();
  const [ambit, setAmbit] = useState<NodePla | null>(null);
  const [estat, setEstat] = useState<EstatDocument | ''>('');
  const [format, setFormat] = useState('');
  const [text, setText] = useState('');
  const [ordre, setOrdre] = useState<{ col: Columna; asc: boolean }>({
    col: 'actualitzat',
    asc: false,
  });
  const [pagina, setPagina] = useState(0);
  const [puja, setPuja] = useState(false);

  const ids = useMemo(
    () => e.departaments.flatMap((d) => d.arees.flatMap((a) => a.serveis.map((s) => s.id))),
    [e.departaments],
  );
  const docs = useDocumentsServeis(ids);

  const dinsAmbit = (d: DocumentAdminDto) => {
    if (!ambit) return true;
    if (ambit.tipus === 'SERVEI') return d.serveiId === ambit.id;
    const area = e.area(e.servei(d.serveiId)?.areaId);
    if (ambit.tipus === 'AREA') return area?.id === ambit.id;
    return area?.departamentId === ambit.id;
  };
  /** A qui va adreçat: el servei, la seva àrea o el seu departament segons l'àmbit. */
  const perA = (d: DocumentAdminDto) => {
    const s = e.servei(d.serveiId);
    const a = e.area(s?.areaId);
    if (d.ambit === 'AREA') return { nom: a?.nom ?? '—', tipus: t('documents.ambits.AREA') };
    if (d.ambit === 'DEPARTAMENT')
      return {
        nom: e.departament(a?.departamentId)?.nom ?? '—',
        tipus: t('documents.ambits.DEPARTAMENT'),
      };
    return { nom: d.servei ?? s?.nom ?? '—', tipus: e.ruta('SERVEI', d.serveiId) };
  };

  const q = normalitza(text.trim());
  const base = (docs.data ?? []).filter(
    (d) =>
      dinsAmbit(d) &&
      (!format || extensio(d) === format) &&
      (!q || normalitza(`${d.titol} ${d.codi ?? ''} ${d.categoria ?? ''}`).includes(q)),
  );
  const compta = (x: EstatDocument) => base.filter((d) => d.estat === x).length;
  const formats = [...new Set((docs.data ?? []).map(extensio).filter(Boolean))].sort();
  const signe = ordre.asc ? 1 : -1;
  const llista = base
    .filter((d) => !estat || d.estat === estat)
    .sort((a, b) => {
      switch (ordre.col) {
        case 'document':
          return signe * a.titol.localeCompare(b.titol);
        case 'per':
          return signe * perA(a).nom.localeCompare(perA(b).nom);
        case 'estat':
          return signe * (ORDRE_ESTAT[a.estat] - ORDRE_ESTAT[b.estat]);
        default:
          return signe * (a.updatedAt ?? '').localeCompare(b.updatedAt ?? '');
      }
    });
  const totalPagines = Math.max(1, Math.ceil(llista.length / PER_PAGINA));
  const paginaActual = Math.min(pagina, totalPagines - 1);
  const visibles = llista.slice(paginaActual * PER_PAGINA, (paginaActual + 1) * PER_PAGINA);
  const filtrant = !!ambit || !!format || !!q || !!estat;

  const capcalera = (col: Columna, etiqueta: string) => {
    const actiu = ordre.col === col;
    return (
      <th aria-sort={actiu ? (ordre.asc ? 'ascending' : 'descending') : undefined}>
        <button
          type="button"
          className="sis-ordena"
          onClick={() => {
            setOrdre({ col, asc: actiu ? !ordre.asc : col !== 'actualitzat' });
            setPagina(0);
          }}
        >
          {etiqueta}
          <Icona nom={actiu ? (ordre.asc ? 'chevron-up' : 'chevron-down') : 'chevron-updown'} />
        </button>
      </th>
    );
  };

  const thDocument = capcalera('document', t('sistema.documents.document'));
  const thPer = capcalera('per', t('sistema.documents.perA'));
  const thActualitzat = capcalera('actualitzat', t('documents.actualitzat'));
  const thEstat = capcalera('estat', t('documents.estat'));

  return (
    <section className="pantalla" data-screen="W-32">
      <Toolbar titol={t('titols.documentacio')}>
        {pot('cai.documents.politica', 'CONSULTAR') ? (
          <Link className="btn btn--outline btn--s" to="/admin/documentacio/politica">
            <Icona nom="sliders" />
            {t('documents.politica')}
          </Link>
        ) : null}
        {pot('cai.documents', 'ALTA') ? (
          <button type="button" className="btn btn--dark btn--s" onClick={() => setPuja(true)}>
            <Icona nom="arrow-up" />
            {t('documents.puja')}
          </button>
        ) : null}
      </Toolbar>
      <div className="content stack">
        <div className="sis-filtres-doc">
          <Cerca
            className="sis-cerca"
            valor={text}
            onCanvia={(v) => {
              setText(v);
              setPagina(0);
            }}
            placeholder={t('sistema.documents.cerca')}
          />
          {ambit ? (
            <span className="chip is-active sis-chip-ambit">
              <span className="dim">
                {t(
                  `marc.estructura.${ambit.tipus === 'DEPARTAMENT' ? 'departament' : ambit.tipus === 'AREA' ? 'area' : 'servei'}`,
                )}
                :
              </span>
              <strong>{ambit.nom}</strong>
              <button
                type="button"
                aria-label={t('sistema.documents.treuAmbit')}
                onClick={() => {
                  setAmbit(null);
                  setPagina(0);
                }}
              >
                <Icona nom="xmark" />
              </button>
            </span>
          ) : (
            <div className="sis-cerca-ambit">
              <CercaAmbit
                placeholder={t('sistema.documents.filtraAmbit')}
                onTria={(n) => {
                  setAmbit(n);
                  setPagina(0);
                }}
              />
            </div>
          )}
          <Desplegable
            variant="chip"
            aria-label={t('documents.format')}
            actiu={format !== ''}
            valor={format}
            opcions={[
              { valor: '', etiqueta: t('sistema.documents.totsFormats') },
              ...formats.map((x) => ({ valor: x, etiqueta: x.toUpperCase() })),
            ]}
            onCanvia={(v) => {
              setFormat(v);
              setPagina(0);
            }}
          />
        </div>
        <div
          className="admin-filtres"
          role="group"
          aria-label={t('documents.estat')}
          style={{ marginBottom: 0 }}
        >
          <button
            type="button"
            className="chip"
            aria-pressed={estat === ''}
            onClick={() => {
              setEstat('');
              setPagina(0);
            }}
          >
            {t('documents.totsN', { count: base.length })}
          </button>
          {ESTATS.map((x) => (
            <button
              key={x}
              type="button"
              className="chip"
              aria-pressed={estat === x}
              onClick={() => {
                setEstat(x);
                setPagina(0);
              }}
            >
              {t(`documents.filtreEstat.${x}`, { count: compta(x) })}
            </button>
          ))}
        </div>
        <div className="panel">
          {docs.isError || e.error ? (
            <ErrorCarrega
              onReintenta={() => {
                e.refetch();
                void docs.refetch();
              }}
            />
          ) : visibles.length === 0 ? (
            <Buit icona="doc-text">
              {docs.isLoading || e.carregant
                ? t('comu.carregant')
                : filtrant
                  ? t('sistema.documents.senseResultats')
                  : t('documents.buit')}
            </Buit>
          ) : (
            <div className="sis-taula-scroll">
              <table className="taula taula--clicable sis-taula-docs">
                <thead>
                  <tr>
                    {thDocument}
                    {thPer}
                    <th>{t('documents.versio')}</th>
                    {thActualitzat}
                    {thEstat}
                    <th style={{ width: 28 }} />
                  </tr>
                </thead>
                <tbody>
                  {visibles.map((d) => {
                    const apagat = d.estat === 'BLOQUEJAT' || d.estat === 'RETIRAT';
                    const v = d.versioVigent ?? d.darreraVersio;
                    const per = perA(d);
                    return (
                      <tr
                        key={d.id}
                        className={apagat ? 'sis-fila-apagada' : undefined}
                        onClick={() => navigate(`/admin/documentacio/${d.id}`)}
                        title={apagat ? t('sistema.documents.noObrible') : undefined}
                      >
                        <td>
                          <Link
                            to={`/admin/documentacio/${d.id}`}
                            className="strong"
                            onClick={(ev) => ev.stopPropagation()}
                          >
                            {d.titol}
                          </Link>
                          <div className="t-caption dim">
                            <span className="mono">{d.codi ?? `#${d.id}`}</span>
                            {v ? ` · ${extensio(d).toUpperCase()} · ${midaLlegible(v.mida)}` : ''}
                            {d.categoria ? ` · ${d.categoria}` : ''}
                          </div>
                        </td>
                        <td>
                          {per.nom}
                          <div className="t-caption dim">{per.tipus}</div>
                        </td>
                        <td className="mono t-caption">
                          {d.versioVigent ? t('documents.vN', { n: d.versioVigent.versio }) : '—'}
                        </td>
                        <td className="dim nowrap">{d.updatedAt ? f.data(d.updatedAt) : '—'}</td>
                        <td>
                          <span className={`badge ${badgeEstatDocument(d.estat)}`}>
                            <Icona nom={ICONA_ESTAT[d.estat]} />
                            {t(`documents.estats.${d.estat}`)}
                          </span>
                          {d.darreraVersio?.estatEscaneig === 'PENDENT' &&
                          d.estat !== 'ESBORRANY' ? (
                            <span className="badge badge--avis" style={{ marginLeft: 4 }}>
                              {t('documents.escaneig.PENDENT')}
                            </span>
                          ) : null}
                          {apagat ? (
                            <div className="t-caption dim">
                              {d.motiuEstat ?? t('sistema.documents.noDescarregable')}
                            </div>
                          ) : null}
                        </td>
                        <td>
                          <Icona nom="chevron-right" className="dim" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {llista.length > PER_PAGINA ? (
            <div className="panel__foot">
              <span className="t-caption dim">
                {t('sistema.documents.nDocuments', { count: llista.length })}
              </span>
              <div className="spacer" />
              <button
                type="button"
                className="btn btn--xs"
                disabled={paginaActual === 0}
                onClick={() => setPagina(paginaActual - 1)}
              >
                {t('comu.anterior')}
              </button>
              <span className="t-caption dim">
                {t('comu.pagina', { actual: paginaActual + 1, total: totalPagines })}
              </span>
              <button
                type="button"
                className="btn btn--xs"
                disabled={paginaActual + 1 >= totalPagines}
                onClick={() => setPagina(paginaActual + 1)}
              >
                {t('comu.seguent')}
              </button>
            </div>
          ) : null}
        </div>
        <p className="admin-nota">
          <Icona nom="lock" />
          {t('documents.notaAmbits')}
        </p>
      </div>
      {puja ? (
        <W34PujaDocument
          serveiId={ambit?.tipus === 'SERVEI' ? ambit.id : null}
          onTanca={() => setPuja(false)}
        />
      ) : null}
    </section>
  );
}
